import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import FoodLibraryPanel from './components/FoodLibraryPanel';
import DayPlanArea from './components/DayPlanArea';
import MacroDashboard from './components/MacroDashboard';
import PlanSettingsPanel from './components/PlanSettingsPanel';

const MEAL_NAMES = ['Breakfast', 'Lunch', 'Dinner', 'Snacks'];

const defaultPlan = () => ({
  name: '',
  clientId: '',
  startDate: '',
  endDate: '',
  targetCalories: 2000,
  targetProtein: 150,
  targetCarbs: 200,
  targetFats: 65,
});

const defaultDayPlan = () => ({
  meals: Object.fromEntries(MEAL_NAMES?.map(m => [m, []]))
});

const TrainerNutritionPlanBuilder = () => {
  const { user } = useAuth();
  const [foodItems, setFoodItems] = useState([]);
  const [customFoodItems, setCustomFoodItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [plan, setPlan] = useState(defaultPlan());
  const [days, setDays] = useState([defaultDayPlan()]);
  const [selectedDay, setSelectedDay] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState('plan');
  const dragFoodRef = useRef(null);

  useEffect(() => {
    loadData();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [user]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [foodRes, clientsRes] = await Promise.all([
        supabase?.from('food_items')?.select('*')?.order('name'),
        supabase?.from('pt_client_assignments')
          ?.select('client_id, user_profiles!pt_client_assignments_client_id_fkey(id, full_name, email)')
          ?.eq('trainer_id', user?.id)
          ?.eq('status', 'active')
      ]);

      setFoodItems(foodRes?.data || []);

      const clientList = clientsRes?.data?.map(a => a?.user_profiles)?.filter(Boolean) || [];
      setClients(clientList);

      if (user?.id) {
        const customRes = await supabase?.from('custom_food_items')?.select('*')?.eq('trainer_id', user?.id);
        setCustomFoodItems(customRes?.data || []);
      }
    } catch (err) {
      console.error('Error loading nutrition builder data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCustomFood = async (foodData) => {
    try {
      const { data, error } = await supabase?.from('custom_food_items')?.insert({
        trainer_id: user?.id,
        ...foodData,
      })?.select()?.single();
      if (!error && data) {
        setCustomFoodItems(prev => [...prev, data]);
      }
    } catch (err) {
      console.error('Error adding custom food:', err);
    }
  };

  const handleDragStartFood = (food) => {
    dragFoodRef.current = food;
  };

  const handleDropOnMeal = (meal, e) => {
    const food = dragFoodRef?.current;
    if (!food) return;
    dragFoodRef.current = null;
    addFoodToMeal(meal, food);
  };

  const handleAddFoodToMeal = (food) => {
    // When clicking + from library, add to first meal (Breakfast) or let user pick
    addFoodToMeal('Breakfast', food);
  };

  const addFoodToMeal = (meal, food) => {
    setDays(prev => {
      const updated = [...prev];
      const day = { ...updated?.[selectedDay] };
      const meals = { ...day?.meals };
      meals[meal] = [
        ...(meals?.[meal] || []),
        {
          id: `${food?.id}-${Date.now()}-${Math.random()}`,
          food,
          quantity: 1,
          foodType: food?.trainer_id ? 'custom' : 'library',
        }
      ];
      day.meals = meals;
      updated[selectedDay] = day;
      return updated;
    });
  };

  const handleRemoveItem = (itemId) => {
    setDays(prev => {
      const updated = [...prev];
      const day = { ...updated?.[selectedDay] };
      const meals = { ...day?.meals };
      MEAL_NAMES?.forEach(meal => {
        meals[meal] = meals?.[meal]?.filter(i => i?.id !== itemId) || [];
      });
      day.meals = meals;
      updated[selectedDay] = day;
      return updated;
    });
  };

  const handleQuantityChange = (itemId, qty) => {
    setDays(prev => {
      const updated = [...prev];
      const day = { ...updated?.[selectedDay] };
      const meals = { ...day?.meals };
      MEAL_NAMES?.forEach(meal => {
        meals[meal] = meals?.[meal]?.map(i => i?.id === itemId ? { ...i, quantity: qty } : i) || [];
      });
      day.meals = meals;
      updated[selectedDay] = day;
      return updated;
    });
  };

  const handleReorderMeal = (meal, newItems) => {
    setDays(prev => {
      const updated = [...prev];
      const day = { ...updated?.[selectedDay] };
      day.meals = { ...day?.meals, [meal]: newItems };
      updated[selectedDay] = day;
      return updated;
    });
  };

  const handleDuplicateDay = () => {
    const currentDay = days?.[selectedDay];
    const newDay = JSON.parse(JSON.stringify(currentDay));
    // Give new unique IDs
    MEAL_NAMES?.forEach(meal => {
      newDay.meals[meal] = newDay?.meals?.[meal]?.map(item => ({
        ...item,
        id: `${item?.food?.id}-${Date.now()}-${Math.random()}`
      }));
    });
    setDays(prev => [...prev, newDay]);
    setSelectedDay(days?.length);
  };

  const handleAddDay = () => {
    setDays(prev => [...prev, defaultDayPlan()]);
    setSelectedDay(days?.length);
  };

  const handleRemoveDay = (idx) => {
    if (days?.length <= 1) return;
    setDays(prev => prev?.filter((_, i) => i !== idx));
    setSelectedDay(Math.max(0, selectedDay - 1));
  };

  const getDailyTotals = () => {
    const currentDay = days?.[selectedDay];
    return Object.values(currentDay?.meals || {})?.flat()?.reduce((acc, item) => ({
      calories: acc?.calories + (item?.food?.calories_per_serving || 0) * item?.quantity,
      protein: acc?.protein + (item?.food?.protein_g_per_serving || 0) * item?.quantity,
      carbs: acc?.carbs + (item?.food?.carbs_g_per_serving || 0) * item?.quantity,
      fats: acc?.fats + (item?.food?.fats_g_per_serving || 0) * item?.quantity,
    }), { calories: 0, protein: 0, carbs: 0, fats: 0 });
  };

  const handleSavePlan = async () => {
    if (!plan?.name || !plan?.clientId) return;
    setIsSaving(true);
    try {
      // Create nutrition plan
      const { data: planData, error: planError } = await supabase?.from('nutrition_plans')?.insert({
        client_id: plan?.clientId,
        trainer_id: user?.id,
        name: plan?.name,
        start_date: plan?.startDate || null,
        end_date: plan?.endDate || null,
        target_calories_per_day: plan?.targetCalories,
        target_protein_g_per_day: plan?.targetProtein,
        target_carbs_g_per_day: plan?.targetCarbs,
        target_fats_g_per_day: plan?.targetFats,
        is_active: true,
      })?.select()?.single();

      if (planError) throw planError;

      // Create days, meals, items
      for (let dayIdx = 0; dayIdx < days?.length; dayIdx++) {
        const dayData = days?.[dayIdx];
        const dayDate = plan?.startDate
          ? new Date(new Date(plan?.startDate).getTime() + dayIdx * 86400000)?.toISOString()?.split('T')?.[0]
          : new Date(Date.now() + dayIdx * 86400000)?.toISOString()?.split('T')?.[0];

        const { data: dayRecord, error: dayError } = await supabase?.from('nutrition_plan_days')?.insert({
          nutrition_plan_id: planData?.id,
          day_date: dayDate,
        })?.select()?.single();

        if (dayError) continue;

        for (let mealIdx = 0; mealIdx < MEAL_NAMES?.length; mealIdx++) {
          const mealName = MEAL_NAMES?.[mealIdx];
          const mealItems = dayData?.meals?.[mealName] || [];

          const { data: mealRecord, error: mealError } = await supabase?.from('nutrition_meals')?.insert({
            nutrition_plan_day_id: dayRecord?.id,
            name: mealName,
            order_index: mealIdx,
          })?.select()?.single();

          if (mealError) continue;

          for (let itemIdx = 0; itemIdx < mealItems?.length; itemIdx++) {
            const item = mealItems?.[itemIdx];
            await supabase?.from('nutrition_items')?.insert({
              nutrition_meal_id: mealRecord?.id,
              food_type: item?.foodType || 'library',
              food_item_id: item?.foodType !== 'custom' ? item?.food?.id : null,
              custom_food_item_id: item?.foodType === 'custom' ? item?.food?.id : null,
              quantity: item?.quantity,
              order_index: itemIdx,
            });
          }
        }
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      // Reset
      setPlan(defaultPlan());
      setDays([defaultDayPlan()]);
      setSelectedDay(0);
    } catch (err) {
      console.error('Error saving nutrition plan:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const dailyTotals = getDailyTotals();
  const targets = {
    calories: plan?.targetCalories,
    protein: plan?.targetProtein,
    carbs: plan?.targetCarbs,
    fats: plan?.targetFats,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">Loading nutrition builder...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Trainer Nutrition Plan Builder - FormForge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />

        {/* Page Header */}
        <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Salad" size={18} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground leading-tight">Nutrition Plan Builder</h1>
                <p className="text-xs text-muted-foreground">Build personalised meal plans with macro tracking</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {saveSuccess && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-success/10 border border-success/30 rounded-lg"
                >
                  <Icon name="CheckCircle" size={14} className="text-success" />
                  <span className="text-xs text-success font-medium">Plan saved!</span>
                </motion.div>
              )}
              <Button size="sm" variant="outline" onClick={handleAddDay}>
                <Icon name="Plus" size={14} className="mr-1" />
                Add Day
              </Button>
              {days?.length > 1 && (
                <Button size="sm" variant="outline" onClick={() => handleRemoveDay(selectedDay)}>
                  <Icon name="Trash2" size={14} className="mr-1" />
                  Remove Day
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Tab Nav */}
        {isMobile && (
          <div className="flex border-b border-border bg-card">
            {[
              { id: 'library', label: 'Foods', icon: 'Apple' },
              { id: 'plan', label: 'Plan', icon: 'CalendarDays' },
              { id: 'macros', label: 'Macros', icon: 'Target' },
            ]?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 py-3 flex flex-col items-center gap-0.5 text-xs font-medium transition-colors ${
                  activeTab === tab?.id ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 container mx-auto px-4 py-4 overflow-hidden">
          {isMobile ? (
            <div className="h-[calc(100vh-200px)]">
              {activeTab === 'library' && (
                <FoodLibraryPanel
                  foodItems={foodItems}
                  customFoodItems={customFoodItems}
                  onAddFood={handleAddFoodToMeal}
                  onAddCustomFood={handleAddCustomFood}
                />
              )}
              {activeTab === 'plan' && (
                <DayPlanArea
                  dayPlan={days?.[selectedDay]}
                  targets={targets}
                  onDrop={handleDropOnMeal}
                  onRemoveItem={handleRemoveItem}
                  onQuantityChange={handleQuantityChange}
                  onReorderMeal={handleReorderMeal}
                  onDuplicateDay={handleDuplicateDay}
                  selectedDay={selectedDay}
                  onSelectDay={setSelectedDay}
                  days={days}
                />
              )}
              {activeTab === 'macros' && (
                <div className="space-y-4">
                  <MacroDashboard dailyTotals={dailyTotals} targets={targets} />
                  <PlanSettingsPanel
                    plan={plan}
                    onUpdate={updates => setPlan(p => ({ ...p, ...updates }))}
                    clients={clients}
                    onSave={handleSavePlan}
                    isSaving={isSaving}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-[280px_1fr_260px] gap-4 h-[calc(100vh-160px)]">
              {/* Left: Food Library */}
              <div className="overflow-hidden">
                <FoodLibraryPanel
                  foodItems={foodItems}
                  customFoodItems={customFoodItems}
                  onAddFood={handleAddFoodToMeal}
                  onAddCustomFood={handleAddCustomFood}
                />
              </div>

              {/* Center: Day Plan */}
              <div className="overflow-hidden flex flex-col">
                <DayPlanArea
                  dayPlan={days?.[selectedDay]}
                  targets={targets}
                  onDrop={handleDropOnMeal}
                  onRemoveItem={handleRemoveItem}
                  onQuantityChange={handleQuantityChange}
                  onReorderMeal={handleReorderMeal}
                  onDuplicateDay={handleDuplicateDay}
                  selectedDay={selectedDay}
                  onSelectDay={setSelectedDay}
                  days={days}
                />
              </div>

              {/* Right: Macros + Settings */}
              <div className="overflow-y-auto space-y-4">
                <MacroDashboard dailyTotals={dailyTotals} targets={targets} />
                <PlanSettingsPanel
                  plan={plan}
                  onUpdate={updates => setPlan(p => ({ ...p, ...updates }))}
                  clients={clients}
                  onSave={handleSavePlan}
                  isSaving={isSaving}
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default TrainerNutritionPlanBuilder;
